import { Component, ReactNode } from 'react'
import * as styles from 'components/features/pfm/OnboardingSummary/OnboardingSummary.css'
import * as cn from 'classnames'
import { Member } from 'interfaces/member'
import { Peergroup } from 'interfaces/peergroups'
import { formatAgeRange, formatCreditRange, formatHousingType, shortenMoney, shortenMoneyRange, shortenNumber } from 'helpers/formatHelpers'
import { DEFAULT_PROP_VALUE } from 'constants/peergroups'
import { getCityStateLabel } from 'helpers/cityHelpers'
import { formatFullCreditscore } from 'helpers/creditHelpers'
import Anchor from 'components/atoms/navigation/Anchor'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import Button from 'components/features/pfm/Button/Button'
import * as isNumber from 'lodash/isNumber'
import { ONBOARDING_SUMMARY, URL_PAGE_MAP } from 'constants/pages'
import CommonLayout from 'components/layouts/CommonLayout'
import GatherHeader from 'components/features/pfm/GatherHeader/GatherHeader'
import Subtitle from 'components/features/pfm/Subtitle/Subtitle'
import { STEPS_COUNT_WITH_FORM } from 'constants/onboarding'
import { Link } from 'react-router-dom'
import CardLayout from 'components/layouts/CardLayout'
import { OPPORTUNITIES_URL } from 'tangle-shared'
import ProgressBar from 'components/features/pfm/ProgressBar/ProgressBar'

type ValueProcessor = (value: any) => ReactNode
type ValueValidator = (value: any) => boolean
const renderValue = (value: any, processor: ValueProcessor, validator: ValueValidator = Boolean) =>
  validator(value) ? processor(value) : DEFAULT_PROP_VALUE

type SubblockDescription<TProps extends Member | Peergroup> = {
  title: string
  getBody: (bodyProps: TProps) => ReactNode
}

export type BlockId = 'age' | 'income'| 'location' | 'housingType' | 'creditscore'

type BlockDescription = {
  id: BlockId
  member: SubblockDescription<Member>
  peers: SubblockDescription<Peergroup>
}

const BLOCKS: BlockDescription[] = [
  {
    id: 'age',
    member: {
      title: 'Your Age',
      getBody: ({ age }) => age,
    },
    peers: {
      title: 'Age Range',
      getBody: ({ age_range }) => renderValue(age_range, formatAgeRange),
    },
  },
  {
    id: 'income',
    member: {
      title: 'Your Income',
      getBody: ({ income }) => renderValue(income, shortenMoney, isNumber),
    },
    peers: {
      title: 'Income Range',
      getBody: ({ income_range }) => renderValue(income_range, shortenMoneyRange),
    },
  },
  {
    id: 'location',
    member: {
      title: 'Your Location',
      getBody: ({ city }) => renderValue(city, getCityStateLabel),
    },
    peers: {
      title: 'Location',
      getBody: ({ location }) => renderValue(location, ({ name }) => name),
    },
  },
  {
    id: 'housingType',
    member: {
      title: 'Your Housing Type',
      getBody: ({ housing_type }) => renderValue(housing_type, formatHousingType),
    },
    peers: {
      title: 'Housing Type',
      getBody: ({ housing_type }) => renderValue(housing_type, formatHousingType),
    },
  },
  {
    id: 'creditscore',
    member: {
      title: 'Your Credit Score',
      getBody: ({ creditscore }) => renderValue(creditscore, formatFullCreditscore, isNumber),
    },
    peers: {
      title: 'Credit Score Range',
      getBody: ({ creditscore_range }) => renderValue(creditscore_range, formatCreditRange),
    },
  },
]

function isMember (bodyProps: Member | Peergroup): bodyProps is Member {
  return isNumber((bodyProps as Member).age)
}

export type OnboardingSummaryStateProps = {
  member: Member
  peers: Peergroup
  returnUrl?: string | null
}

export type OnboardingSummaryProps = OnboardingSummaryStateProps

class OnboardingSummary extends Component<OnboardingSummaryProps> {

  render () {
    const { member, peers } = this.props

    return (
      <Anchor placement={ ONBOARDING_SUMMARY } qaMarker='onboarding-summary'>
        <CommonLayout logoSize='responsive-small'>
          <div className={ styles.profileCompact }>
            <ProgressBar progress={ 100 } />
          </div>

          <CardLayout>
            <header className={ cn(styles.section, styles.header) }>
              <GatherHeader
                title='Success! We found your peers.'
                step={ 5 }
                maxStep={ STEPS_COUNT_WITH_FORM }
              >
                We have used your profile information to successfully build your peer group.
              </GatherHeader>
            </header>

            <section className={ styles.compareHeader }>
              <div className={ styles.you }>
                <span>You</span>
              </div>
              <div className={ styles.peers }>
                <span>Your Peers:</span>
                <span className={ styles.peersSubtitle } >
                  { shortenNumber(peers.size) } people
                </span>
              </div>
            </section>

            <section className={ cn(styles.section, styles.compare) }>
              <section className={ styles.compareLeft }>
                <div className={ styles.blocks }>
                  { BLOCKS.map(
                    (block) => this.shouldRenderBlock(block)
                      ? this.renderBlock<Member>(block, member)
                      : null,
                  ) }
                </div>
              </section>

              <div className={ styles.divider } />

              <section className={ styles.compareRight }>
                <div className={ styles.blocks }>
                  {
                    BLOCKS.map(
                      (block) => this.shouldRenderBlock(block) ? this.renderBlock<Peergroup>(block, peers) : null
                    )
                  }
                </div>
              </section>
            </section>

            <section className={ styles.toDashboard }>
              { this.renderNextPageBlock() }
            </section>

            <section className={ cn(styles.section, styles.toSettings) }>
              <Subtitle>
                If anything seems incorrect you can edit
                your profile on your <NavigationLink to='settings'>settings</NavigationLink> page.
              </Subtitle>
            </section>
          </CardLayout>
        </CommonLayout>
      </Anchor>
    )
  }


  renderBlock = <TBodyProps extends Member | Peergroup>(block: BlockDescription, bodyProps: TBodyProps) => {
    const kind = isMember(bodyProps) ? 'member' : 'peers'
    const specificBlock = block[kind] as SubblockDescription<TBodyProps>

    return (
      <div
        key={ block.id }
        className={ styles.block }
        data-qa-marker={ block.id }
        data-qa-type={ `block-${ kind }` }
      >
        <div className={ styles.blockTitle }>{ specificBlock.title }</div>
        <div className={ styles.blockBody }>{ specificBlock.getBody(bodyProps) }</div>
      </div>
    )
  }

  private renderNextPageBlock = () => {
    const { returnUrl } = this.props

    const isBackOpportunityPage = Boolean(returnUrl && returnUrl === OPPORTUNITIES_URL)
    const buttonChild = this.renderNextPageButton(isBackOpportunityPage)

    const pageName = returnUrl && URL_PAGE_MAP[returnUrl]
    if (pageName || !returnUrl) {
      return (
        <NavigationLink to={ pageName || 'dashboard' }>
          { buttonChild }
        </NavigationLink>
      )
    }

    return (
      <Link to={ returnUrl }>
        { buttonChild }
      </Link>
    )
  }

  private renderNextPageButton = (isBackOpportunityPage: boolean) => {
    const title = isBackOpportunityPage ? 'Opportunities' : 'Money'
    return (
      <Button fullWidth>
        Go to Your { title }
      </Button>
    )
  }

  private shouldRenderBlock = (block: BlockDescription) => {
    const { member } = this.props
    const creditscoreExist = isNumber(member.creditscore)

    return block.id !== 'creditscore' || creditscoreExist
  }
}

export default OnboardingSummary
